import { useAuthStore } from '../../stores/auth-store';

interface AdvanceBarProps {
  consumed: number;
  total: number;
  variant?: 'full' | 'compact';
  /** 'consumption' = green→orange→red (higher is worse). 'progress' = always blue (higher is better). */
  mode?: 'consumption' | 'progress';
  className?: string;
}

function getColor(pct: number, mode: 'consumption' | 'progress'): { bar: string; text: string; label: string } {
  if (mode === 'progress') {
    return { bar: 'bg-blue-500', text: 'text-blue-600', label: 'Progreso' };
  }
  if (pct >= 90) return { bar: 'bg-red-500', text: 'text-red-600', label: 'Alerta Crítica' };
  if (pct >= 80) return { bar: 'bg-orange-500', text: 'text-orange-600', label: 'Alerta' };
  return { bar: 'bg-green-500', text: 'text-green-600', label: 'Normal' };
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('es-EC', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(amount);
}

/**
 * AdvanceBar — Progress bar showing budget consumption.
 * Hidden from Residente role per C-24.
 */
export function AdvanceBar({ consumed, total, variant = 'full', mode = 'consumption', className = '' }: AdvanceBarProps) {
  const user = useAuthStore((s) => s.user);

  // C-24: Hidden from Residente de Obra
  if (user?.role === 'residente') return null;

  // In 'progress' mode, allow > 100% (executed can exceed proforma in Scenario B).
  // In 'consumption' mode, cap at 100% for bar display (budget alert thresholds apply to real %).
  const rawPct = total > 0 ? (consumed / total) * 100 : 0;
  const pct = mode === 'progress' ? rawPct : Math.min(100, rawPct);
  // Bar width is always capped at 100% for CSS rendering; the label shows the real percentage.
  const barWidth = Math.min(100, pct);
  const color = getColor(pct, mode);

  if (variant === 'compact') {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <div className="h-2 w-16 overflow-hidden rounded-full bg-gray-200">
          <div className={`h-full ${color.bar} transition-all`} style={{ width: `${barWidth}%` }} />
        </div>
        <span className={`text-xs font-semibold ${color.text}`}>{pct.toFixed(0)}%</span>
      </div>
    );
  }

  return (
    <div className={`space-y-1 ${className}`}>
      <div className="flex items-center justify-between text-xs">
        <span className="text-gray-500">
          {formatCurrency(consumed)} / {formatCurrency(total)}
        </span>
        <span className={`font-semibold ${color.text}`}>{pct.toFixed(1)}%</span>
      </div>
      <div className="h-2.5 w-full overflow-hidden rounded-full bg-gray-200">
        <div
          className={`h-full rounded-full ${color.bar} transition-all duration-300`}
          style={{ width: `${barWidth}%` }}
        />
      </div>
    </div>
  );
}
